import axios from "axios";

let BASE_URL;
let authContextRef = null;
let pendingRequest = null; // Store the request that triggered re-auth

// Function to set the auth context reference
export const setAuthContextRef = (authContext) => {
  authContextRef = authContext;
};

// Function to update the configuration
export const configureApiClient = (generalEndPoint) => {
  BASE_URL = generalEndPoint;
  // Update the baseURL of the existing instance
  if (apiClient.defaults) {
    apiClient.defaults.baseURL = generalEndPoint;
  }
};

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  /*withCredentials: true,*/
});

// Add request interceptor to automatically include JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log("🔴 Response error:", {
      status: error.response?.status,
      message: error.response?.data?.message,
      url: error.config?.url,
      authContextRef: authContextRef,
      hasReAuthMethod: authContextRef?.triggerReAuth ? "YES" : "NO",
    });

    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      console.log("🔴 Got 401 - checking if we should show re-auth modal");

      // Don't trigger re-auth on login page itself
      if (window.location.pathname.includes("/login")) {
        console.log("🔴 On login page - not showing re-auth modal");
        return Promise.reject(error);
      }

      // Check if this is an authentication failure
      const errorMessage = error.response.data?.message || "";
      const isAuthenticationFailed =
        errorMessage.toLowerCase() === "authentication failed";

      // Check if we've already tried re-auth for this request
      if (error.config._retry) {
        console.log("🔴 Already tried re-auth for this request, not retrying");
        return Promise.reject(error);
      }

      if (
        isAuthenticationFailed &&
        authContextRef &&
        authContextRef.triggerReAuth
      ) {
        // Mark this request as having triggered re-auth
        error.config._retry = true;

        // Store the original request config
        pendingRequest = error.config;

        // Create a promise that will resolve when re-authentication is complete
        return new Promise((resolve, reject) => {
          // Store the resolve and reject functions to be called later
          pendingRequest._resolve = resolve;
          pendingRequest._reject = reject;

          // Trigger the re-authentication modal
          authContextRef.triggerReAuth();
        });
      }
    }

    return Promise.reject(error);
  }
);

// Function to retry the pending request after successful re-authentication
export const retryPendingRequest = async () => {
  if (pendingRequest && pendingRequest._resolve) {
    try {
      console.log("🟢 Retrying pending request after re-authentication");

      // Get the new token
      const newToken = sessionStorage.getItem("token");
      if (newToken) {
        // Update the authorization header with the new token
        pendingRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      // Retry the original request
      const response = await apiClient.request(pendingRequest);

      // Resolve the original promise with the successful response
      pendingRequest._resolve(response);

      // Clear the pending request
      const temp = pendingRequest;
      pendingRequest = null;

      return response;
    } catch (error) {
      console.error(
        "🔴 Failed to retry request after re-authentication:",
        error
      );

      // Reject the original promise
      if (pendingRequest._reject) {
        pendingRequest._reject(error);
      }

      // Clear the pending request
      pendingRequest = null;

      throw error;
    }
  }
};

// Function to cancel pending request (e.g., if user cancels re-auth)
export const cancelPendingRequest = () => {
  if (pendingRequest && pendingRequest._reject) {
    pendingRequest._reject(new Error("Re-authentication cancelled"));
    pendingRequest = null;
  }
};

export default apiClient;
export { BASE_URL };
